import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useUpsertPageSectionMutation } from "../api/businessApi";

const PageSectionEditor = ({ isOpen, onClose, pageKey, sectionKey, label, content, onSuccess }) => {
  const [localContent, setLocalContent] = useState({});
  const [upsertSection, { isLoading }] = useUpsertPageSectionMutation();

  useEffect(() => {
    if (content) {
      setLocalContent(JSON.parse(JSON.stringify(content)));
    }
  }, [content]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      await upsertSection({ pageKey, sectionKey, content: localContent }).unwrap();
      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Section content has been updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.data?.message || "Failed to save section content.",
      });
    }
  };

  // Determine if a key should use a textarea
  const isTextArea = (key) => {
    const textKeys = ["body", "description", "subtitle", "text", "content", "bio", "paragraph"];
    return textKeys.some((tk) => key.toLowerCase().includes(tk));
  };

  // Update a value at a given path in localContent
  const updateValue = (path, value) => {
    setLocalContent((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (let i = 0; i < path.length - 1; i++) {
        obj = obj[path[i]];
      }
      obj[path[path.length - 1]] = value;
      return updated;
    });
  };

  // Add item to an array at a given path
  const addArrayItem = (path, template) => {
    setLocalContent((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (let i = 0; i < path.length; i++) {
        obj = obj[path[i]];
      }
      obj.push(typeof template === "object" ? { ...template } : template);
      return updated;
    });
  };

  // Remove item from array at a given path + index
  const removeArrayItem = (path, index) => {
    setLocalContent((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (let i = 0; i < path.length; i++) {
        obj = obj[path[i]];
      }
      obj.splice(index, 1);
      return updated;
    });
  };

  // Create a blank template from an existing object
  const createTemplate = (item) => {
    if (typeof item === "string") return "";
    if (typeof item === "object" && item !== null) {
      const template = {};
      for (const key of Object.keys(item)) {
        if (typeof item[key] === "string") template[key] = "";
        else if (typeof item[key] === "boolean") template[key] = false;
        else if (typeof item[key] === "number") template[key] = 0;
        else template[key] = "";
      }
      return template;
    }
    return "";
  };

  // Format a key into a human-readable label
  const formatLabel = (key) => {
    return String(key)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Render fields recursively
  const renderField = (key, value, path) => {
    const currentPath = [...path, key];

    // Boolean
    if (typeof value === "boolean") {
      return (
        <div key={currentPath.join(".")} className="flex items-center gap-3 py-2">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => updateValue(currentPath, e.target.checked)}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label className="text-sm font-medium text-gray-700">{formatLabel(key)}</label>
        </div>
      );
    }

    // String
    if (typeof value === "string") {
      return (
        <div key={currentPath.join(".")} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          {isTextArea(key) ? (
            <textarea
              value={value}
              onChange={(e) => updateValue(currentPath, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => updateValue(currentPath, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          )}
        </div>
      );
    }

    // Number
    if (typeof value === "number") {
      return (
        <div key={currentPath.join(".")} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formatLabel(key)}
          </label>
          <input
            type="number"
            value={value}
            onChange={(e) => updateValue(currentPath, Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        </div>
      );
    }

    // Array of objects
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      return (
        <div key={currentPath.join(".")} className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-gray-800">
              {formatLabel(key)} ({value.length} items)
            </label>
            <button
              type="button"
              onClick={() => addArrayItem(currentPath, createTemplate(value[0]))}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Item
            </button>
          </div>
          <div className="space-y-4">
            {value.map((item, index) => (
              <div key={index} className="relative bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeArrayItem(currentPath, index)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                </div>
                {Object.entries(item).map(([itemKey, itemValue]) =>
                  renderField(itemKey, itemValue, [...currentPath, index])
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Array of strings
    if (Array.isArray(value)) {
      return (
        <div key={currentPath.join(".")} className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-gray-800">
              {formatLabel(key)} ({value.length} items)
            </label>
            <button
              type="button"
              onClick={() => addArrayItem(currentPath, "")}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Item
            </button>
          </div>
          <div className="space-y-2">
            {value.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => updateValue([...currentPath, index], e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder={`Item ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem(currentPath, index)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Nested object (non-array)
    if (typeof value === "object" && value !== null) {
      return (
        <div key={currentPath.join(".")} className="mb-6">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            {formatLabel(key)}
          </label>
          <div className="pl-4 border-l-2 border-indigo-200">
            {Object.entries(value).map(([subKey, subValue]) =>
              renderField(subKey, subValue, currentPath)
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center p-4 sm:p-6">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl my-8">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 rounded-t-xl px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Edit: {label || formatLabel(sectionKey)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {pageKey} / {sectionKey}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {Object.entries(localContent).map(([key, value]) =>
              renderField(key, value, [])
            )}
            {Object.keys(localContent).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No editable content found for this section.</p>
              </div>
            )}
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-xl px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageSectionEditor;
